import React from 'react';
import { X, Crown, CheckCircle2, Phone, Tag } from 'lucide-react';
import { Button } from './ui/button';
import { useAuthContext } from '../contexts/AuthContext';
import { Input } from './ui/input';

interface BuyPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onViewPlans: () => void;
  errorMessage?: string;
  remainingViews?: number;
}

export default function BuyPlanModal({ 
  isOpen, 
  onClose, 
  onViewPlans, 
  errorMessage,
  remainingViews = 0 
}: BuyPlanModalProps) {
  const { user } = useAuthContext();
  const [copiedCode, setCopiedCode] = React.useState(false);
  
  if (!isOpen) return null;

  // Determine the modal type based on error message
  const isNoSubscription = errorMessage?.toLowerCase().includes('subscription required');
  const isInactiveSubscription = errorMessage?.toLowerCase().includes('not active');
  const isNoContactViews = errorMessage?.toLowerCase().includes('no contact views');
  const isNotInitialized = errorMessage?.toLowerCase().includes('not initialized');

  // Check if user has referral code from registration
  const hasReferralCode = user?.referredBy;

  const handleCopyReferralCode = () => {
    if (user?.referredBy) {
      navigator.clipboard.writeText(user.referredBy);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const getModalContent = () => {
    if (isNoSubscription) {
      return {
        icon: <Crown className="size-12 text-amber-500" />,
        title: 'Unlock Contact Details',
        message: 'Get access to phone numbers and email addresses with a premium subscription',
        benefits: [
          'View unlimited contact details',
          'Connect with potential matches',
          'Priority messaging',
          'Advanced search filters'
        ],
        buttonText: 'View Plans',
        gradient: 'from-amber-500 to-orange-500'
      };
    }

    if (isInactiveSubscription) {
      return {
        icon: <Crown className="size-12 text-rose-500" />,
        title: 'Subscription Inactive',
        message: 'Your subscription has expired. Renew now to continue accessing contact details',
        benefits: [
          'Restore full access',
          'View contact information',
          'Send unlimited messages',
          'Get profile visibility boost'
        ],
        buttonText: 'Renew Subscription',
        gradient: 'from-rose-500 to-pink-500'
      };
    }

    if (isNoContactViews) {
      return {
        icon: <Phone className="size-12 text-blue-500" />,
        title: 'Contact Views Exhausted',
        message: `You've used all your contact views. Upgrade your plan to get more`,
        benefits: [
          'Get more contact views',
          'Upgrade to unlimited plan',
          'View profiles without limits',
          'Premium member badge'
        ],
        buttonText: 'Upgrade Plan',
        gradient: 'from-blue-500 to-indigo-500'
      };
    }

    // Default fallback
    return {
      icon: <Crown className="size-12 text-purple-500" />,
      title: 'Premium Feature',
      message: 'Upgrade to access contact details and connect with matches',
      benefits: [
        'View contact information',
        'Send unlimited messages',
        'Advanced search options',
        'Profile boost'
      ],
      buttonText: 'View Plans',
      gradient: 'from-purple-500 to-pink-500'
    };
  };

  const content = getModalContent();

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-in fade-in duration-200"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md mx-4 animate-in zoom-in-95 fade-in duration-200">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header with Gradient */}
          <div className={`bg-gradient-to-br ${content.gradient} p-8 text-white relative`}>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 size-8 rounded-full bg-white/20 hover:bg-white/30 transition-colors flex items-center justify-center"
            >
              <X className="size-5" />
            </button>
            
            <div className="flex flex-col items-center text-center">
              <div className="size-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4">
                {content.icon}
              </div>
              <h2 className="text-2xl mb-2">{content.title}</h2>
              <p className="text-white/90 text-sm">{content.message}</p>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Benefits List */}
            <div className="space-y-3 mb-6">
              <p className="text-sm text-gray-600 mb-3">Premium benefits include:</p>
              {content.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="size-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>

            {/* Remaining Views Info (if applicable) */}
            {remainingViews > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4">
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">{remainingViews}</span> contact view{remainingViews !== 1 ? 's' : ''} remaining in your plan
                </p>
              </div>
            )}

            {/* Error Message (if different from standard ones) */}
            {errorMessage && !isNoSubscription && !isInactiveSubscription && !isNoContactViews && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                <p className="text-sm text-amber-800">{errorMessage}</p>
              </div>
            )}

            {/* Referral Code Section */}
            {hasReferralCode && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="size-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <Tag className="size-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-900 mb-1">🎉 You Have a Referral Discount!</p>
                    <p className="text-xs text-green-700 mb-2">
                      Apply this code when buying a plan to get a special discount
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-white border-2 border-dashed border-green-400 rounded-lg px-3 py-2">
                        <p className="text-lg font-bold text-green-800 text-center tracking-wider">
                          {user?.referredBy}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopyReferralCode}
                        className="border-green-500 text-green-700 hover:bg-green-50"
                      >
                        {copiedCode ? '✓ Copied!' : 'Copy'}
                      </Button>
                    </div>
                    <p className="text-xs text-green-600 mt-2">
                      💡 This code will be automatically shown when you go to buy a plan
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Maybe Later
              </Button>
              <Button
                onClick={() => {
                  onClose();
                  onViewPlans();
                }}
                className={`flex-1 bg-gradient-to-r ${content.gradient} hover:opacity-90 text-white border-0`}
              >
                {content.buttonText}
              </Button>
            </div>

            {/* Trust Badge */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                Trusted by 10,000+ happy couples
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}